/**
 * MSW Override: Booking Payment 3DS Challenge scenario
 *
 * The payment call answers 200 HOLD/UNPAID with a CHALLENGE_REQUIRED
 * paymentAuthentication block whose challengeRedirectUrl is the pay page itself
 * with payment_redirected=true (F4's return route; no bank involved). GET of that
 * booking reads CHALLENGE_REQUIRED until the return notification has been posted
 * once, then CONFIRMED/PAID/AUTHENTICATED: a page that forgets the notification
 * stays on CHALLENGE_REQUIRED, as it would against the real backend.
 * Bookings never paid here fall through to the next handler.
 *
 * Enable by setting ENABLE_MSW=true and
 * NUXT_PUBLIC_MSW_OVERRIDES_BOOKING_PAYMENT_3DS_CHALLENGE=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern, createMockBookingResponse, createMockPaymentAuthenticationResponse, type BookingResponse } from '@cvo/domain';

// Must match PAYMENT_REDIRECTED_QUERY_PARAM in app/types/payment. F4's return route is
// /book/pay/{bookingId}?payment_redirected=true: the pay page the customer pays from.
const PAYMENT_REDIRECTED_QUERY_PARAM = 'payment_redirected';

/** Device data already collected: the state every block carries once the payment call is made. */
export const DDC_COLLECTED_PROVIDER_DATA = { ddcIframeUrl: 'about:blank', ddcCollected: true, ddcExpired: false };

function createChallengeRequiredBlock() {
  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.set(PAYMENT_REDIRECTED_QUERY_PARAM, 'true');

  return createMockPaymentAuthenticationResponse({
    status: 'CHALLENGE_REQUIRED',
    providerData: { ...DDC_COLLECTED_PROVIDER_DATA, challengeRedirectUrl: returnUrl.toString() },
  });
}

function createChallengedBooking(bookingId: string) {
  return createMockBookingResponse({
    id: bookingId,
    bookingStatus: 'HOLD',
    paymentStatus: 'UNPAID',
    paymentAuthentication: createChallengeRequiredBlock(),
  });
}

const NOTIFIED_STORAGE_KEY = 'msw:booking-payment-3ds:notified';

/** Notified state per booking: in sessionStorage in the browser (the return reloads the page), in memory in node. */
function createNotifiedByBookingStore() {
  const memory = new Map<string, boolean>();
  const storage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage;

  function read(): Map<string, boolean> {
    if (!storage) return memory;
    return new Map(Object.entries(JSON.parse(storage.getItem(NOTIFIED_STORAGE_KEY) ?? '{}') as Record<string, boolean>));
  }

  return {
    has: (bookingId: string) => read().has(bookingId),
    get: (bookingId: string) => read().get(bookingId),
    set(bookingId: string, notified: boolean) {
      const next = read().set(bookingId, notified);
      storage?.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify(Object.fromEntries(next)));
    },
  };
}

/**
 * The challenge round-trip shared by the challenge and timeout scenarios: pay → HOLD +
 * CHALLENGE_REQUIRED; GET reads that until the return notification lands; then `settle` decides.
 */
export function createChallengeRoundTripOverrides(scenario: string, baseUrl: string, settle: (bookingId: string) => BookingResponse): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');
  const notifiedByBooking = createNotifiedByBookingStore();

  return [
    http.post(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_PAYMENTS)}`,
      ({ params }) => {
        const bookingId = params.bookingId as string;
        notifiedByBooking.set(bookingId, false);
        console.log(`[MSW] ${scenario}: HOLD + CHALLENGE_REQUIRED for booking`, bookingId);

        return HttpResponse.json(createChallengedBooking(bookingId));
      },
    ),
    http.post(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS)}`,
      ({ params }) => {
        const bookingId = params.bookingId as string;
        if (!notifiedByBooking.has(bookingId)) return; // not paid here: fall through to the next handler

        notifiedByBooking.set(bookingId, true);
        console.log(`[MSW] ${scenario}: return notification received for booking`, bookingId);

        return new HttpResponse(null, { status: 202 });
      },
    ),
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING)}`,
      ({ params }) => {
        const bookingId = params.bookingId as string;
        const notified = notifiedByBooking.get(bookingId);
        if (notified === undefined) return; // not paid here: fall through to the next handler

        if (!notified) {
          console.log(`[MSW] ${scenario}: still CHALLENGE_REQUIRED, no notification yet for booking`, bookingId);
          return HttpResponse.json(createChallengedBooking(bookingId));
        }

        console.log(`[MSW] ${scenario}: settled for booking`, bookingId);
        return HttpResponse.json(settle(bookingId));
      },
    ),
  ];
}

export function createBookingPayment3dsChallengeOverrides(baseUrl: string): HttpHandler[] {
  return createChallengeRoundTripOverrides('booking payment 3ds challenge', baseUrl, bookingId =>
    createMockBookingResponse({
      id: bookingId,
      bookingStatus: 'CONFIRMED',
      paymentStatus: 'PAID',
      paymentAuthentication: createMockPaymentAuthenticationResponse({ status: 'AUTHENTICATED', providerData: DDC_COLLECTED_PROVIDER_DATA }),
    }));
}
